import get from 'lodash/get';
import dayjs from 'dayjs';
import { BLOCKS } from '@contentful/rich-text-types';
import {
  BIG_TABLET_RESOLUTION,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TABLET_RESOLUTION,
  FEEDBACK_FORM_FIELDS,
  FULL_HD_RESOLUTION,
  HORIZONTAL_MOBILE,
  IMAGES,
  PAGES,
  PHONE_RESOLUTION,
} from 'utils/constants';
import gaHelper from 'utils/ga';
import { handleError } from './error';

export const themes = {
  dark: {
    main: '#FFF',
    secondary: '#000',
  },
  light: {
    main: '#000',
    secondary: '#FFF',
  },
};

export const addThousandsSeparators = (value) => value.toString()
  .replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const toInt = (str) => parseInt(str, 10);

export const removeThousandsSeparators = (value) => toInt(value.replaceAll(',', ''));

export const validateEmail = (email) => {
  // eslint-disable-next-line max-len
  const reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-\d]+\.)+[a-zA-Z]{2,}))$/;

  if (reg.test(email) === false) {
    return false;
  }

  return reg.test(email);
};

export function getYoutubeVideoIdFromUrl(url) {
  try {
    const parsed = new URL(url);

    // 1. Standard watch URL: ?v=VIDEO_ID
    if (parsed.searchParams.has('v')) {
      return parsed.searchParams.get('v');
    }

    // 2. youtu.be short links
    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.slice(1).split('?')[0];
    }

    // 3. /embed/VIDEO_ID
    if (parsed.pathname.startsWith('/embed/')) {
      return parsed.pathname.split('/embed/')[1].split('/')[0];
    }

    // 4. /shorts/VIDEO_ID
    if (parsed.pathname.startsWith('/shorts/')) {
      return parsed.pathname.split('/shorts/')[1].split('/')[0];
    }

    // 5. Fallback: last path segment without query params
    return parsed.pathname.split('/').pop().split('?')[0];
  } catch {
    return null;
  }
}

export const mobileResolution = toInt(PHONE_RESOLUTION);
export const fullResolution = toInt(FULL_HD_RESOLUTION);
export const horizontalPhone = toInt(HORIZONTAL_MOBILE);
export const tabletResolution = toInt(BIG_TABLET_RESOLUTION);
export const smallTabletResolution = toInt(DEFAULT_TABLET_RESOLUTION);

export const setOverflowForBody = (isHidden) => {
  document.body.style.overflow = isHidden ? 'hidden' : 'initial';
};

export const getMainLinksForSitemap = (updatedAt) => [
  {
    path: '/',
    updatedAt,
  },
  {
    path: `/${PAGES.portfolio}`,
    updatedAt,
  },
  {
    path: `/${PAGES.process}`,
    updatedAt,
  },
  {
    path: `/${PAGES.company}`,
    updatedAt,
  },
  {
    path: `/${PAGES.whyUs}`,
    updatedAt,
  },
  {
    path: `/${PAGES.contact}`,
    updatedAt,
  },
  {
    path: `/${PAGES.blog}`,
    updatedAt,
  },
  {
    path: `/${PAGES.customChatApp}`,
    updatedAt,
  },
  {
    path: `/${PAGES.customMobileApp}`,
    updatedAt,
  },
  {
    path: `/${PAGES.customWebApp}`,
    updatedAt,
  },
  {
    path: `/${PAGES.crowdfundingPlatform}`,
    updatedAt,
  },
  {
    path: `/${PAGES.erpDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.designServices}`,
    updatedAt,
  },
  {
    path: `/${PAGES.iosDevelopmentServices}`,
    updatedAt,
  },
  {
    path: `/${PAGES.mlDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.cloudDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.androidDevelopmentServices}`,
    updatedAt,
  },
  {
    path: `/${PAGES.mvpDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.lendingSoftwareDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.paymentGatewayDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.billingSoftwareDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.fintechDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.softwareQualityAssuranceServices}`,
    updatedAt,
  },
  {
    path: `/${PAGES.devOpsDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.aiDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.privacyPolicy}`,
    updatedAt,
  },
  {
    path: `/${PAGES.termsAndConditions}`,
    updatedAt,
  },
  {
    path: `/${PAGES.discoveryPhase}`,
    updatedAt,
  },
  {
    path: `/${PAGES.crossPlatformDevelopmentServices}`,
    updatedAt,
  },
  {
    path: `/${PAGES.dataScienceDevelopment}`,
    updatedAt,
  },
  {
    path: `/${PAGES.prototypingServices}`,
    updatedAt,
  },
  {
    path: `/${PAGES.tradingSoftwareDevelopment}`,
    updatedAt,
  },
];

export const rootUrl = process.env.NODE_ENV === 'development'
  ? process.env.NEXT_PUBLIC_DEV_URL
  : process.env.NEXT_PUBLIC_PROD_URL;

export const isServer = typeof window === 'undefined';

const getQueryString = (params) => params && Object
  .keys(params)
  .filter((key) => `${key}=${params[key]}`)
  .join('&');

export const getOptimizedContentfulImage = (imageUrl, {
  width,
  height,
  fm = 'jpg',
  fl = 'progressive',
}) => {
  if (!imageUrl) {
    return '';
  }

  const queryString = getQueryString({
    w: width,
    h: height,
    fm,
    fl,
  });

  return queryString
    ? `${imageUrl}?${queryString}`
    : imageUrl;
};

export const createMarkup = (data) => ({ __html: data });

export const addHttpsToUrl = (url) => (/^\/\//.test(url) ? `https:${url}` : url);

export const getFileUrl = (file) => addHttpsToUrl(get(file, 'fields.file.url', ''));

export const getImage = (file) => {
  const imageData = get(file, 'fields', {});

  const url = addHttpsToUrl(imageData.file?.url);
  const alt = imageData.description || imageData.title || '';

  return {
    ...imageData.file?.details.image,
    alt,
    url,
  };
};

export const getItemsByType = (contentModules, type, fields = []) => {
  if (type) {
    const results = contentModules?.filter((module) => {
      const moduleType = get(module, 'sys.contentType.sys.id', null);

      return moduleType === type;
    });

    if (fields.length) {
      return (results || []).map((res) => fields.reduce((acc, field) => {
        if (!field) return acc;

        acc[field] = get(res, `fields.${field}`, null);

        return acc;
      }, {}));
    }

    return (results || []).map((module) => get(module, 'fields', null));
  }

  return null;
};

export const getDocumentFields = (document, fields = [], { isNormilized = false } = {}) => {
  if (fields.length) {
    return fields.reduce((acc, field) => {
      if (!field) return acc;

      if (field.indexOf('sys.') === 0 || isNormilized) {
        acc[field] = get(document, field, null);
      } else {
        acc[field] = get(document, `fields.${field}`, null);
      }

      return acc;
    }, {});
  }

  return get(document, 'fields', null);
};

export const hoursToMs = (duration) => (duration * 60 * 60 * 1000);

export const getDateTime = () => (new Date()).getTime();

export const setDataToLocalStorageWithExpire = (key, value, expiration) => {
  const item = {
    value,
    expiry: getDateTime() + expiration,
  };

  localStorage.setItem(key, JSON.stringify(item));
};

export const getDataFromLocalStorageWithExpire = (key) => {
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return null;
  }

  const {
    value,
    expiry,
  } = JSON.parse(itemStr);

  if (getDateTime() > expiry) {
    localStorage.removeItem(key);

    return null;
  }

  return value;
};

export const artificialDelay = (t) => new Promise(((resolve) => {
  setTimeout(() => {
    resolve();
  }, t);
}));

export const getFeedbackFormData = (data) => {
  const formData = new window.FormData();
  const formDataArray = Object.entries(FEEDBACK_FORM_FIELDS);

  formDataArray.forEach(([key]) => {
    // TODO rewrite it
    switch (key) {
    case FEEDBACK_FORM_FIELDS.attachments: {
      if (data[key]) {
        [...data[key]].forEach((file) => formData.append('attachments', file));
      }

      break;
    }
    case FEEDBACK_FORM_FIELDS.projectBudget: {
      if (data[key]) {
        formData.append(key, removeThousandsSeparators(data[key]));
      }

      break;
    }
    case FEEDBACK_FORM_FIELDS.clientId: {
      const clientId = gaHelper.getClientId();

      formData.append(key, clientId);

      break;
    }
    default: {
      if (data[key]) {
        formData.append(key, data[key]);
      }

      break;
    }
    }
  });

  return formData;
};

export const isNumeric = (value) => !isNaN(value);

export const getPathWithCdn = (path) => (`${path}`);
// TODO: Uncomment when cdn will be fixed
// export const getPathWithCdn = (path) => (process.env.EDGE_URL ? `${process.env.EDGE_URL}${path}` : path);

// export const addCdnToImages = (images) => Object.entries(images).reduce((acc, [key, value]) => {
//   isObject(value)
//     ? acc[key] = addCdnToImages(value)
//     : acc[key] = getPathWithCdn(value);
//
//   return acc;
// }, {});

export const staticImagesUrls = IMAGES;

export const getConvertedFileSize = (size) => {
  const kilobytes = (size / 1024).toFixed(2);

  return kilobytes > 1024
    ? `${(kilobytes / 1000).toFixed(2)} MB`
    : `${kilobytes} kB`;
};

export const serverSideRedirect = ({ res }, {
  status = 302,
  path = '/',
}) => {
  if (res) {
    res.writeHead(status, { Location: path });
    res.end();
  }
};

export const formatDate = (date, { format = DEFAULT_DATE_FORMAT } = {}) => dayjs(date)
  .format(format);

export const getMaxVal = (...args) => args.reduce((currentMax, val) => (val > currentMax ? val : currentMax), args[0]);

export const getLimitedList = (list, {
  start = 0,
  limit = 1,
}) => list.slice(start, limit);

export const runMiddleware = (req, res, fn) => new Promise((resolve, reject) => {
  fn(req, res, (result) => {
    if (result instanceof Error) {
      return reject(result);
    }

    return resolve(result);
  });
});

export const formParser = (form) => async (req, _, next) => {
  form.parse(req, (err, fields, files) => {
    if (!err) {
      req.body = fields;
      req.files = files;
    } else {
      handleError({
        err,
        message: 'Error in the bodyParser function',
      });
    }

    next();
  });
};

export const findBlock = (blocks, blockType) => blocks.find((module) => {
  const { slug } = getDocumentFields(module, ['slug']);

  return slug === blockType;
});

export const extractContentfulTableData = (richTextContent) => {
  const tables = [];
  richTextContent?.content.forEach((node) => {
    if (node.nodeType === BLOCKS.TABLE) {
      const tableRows = [];
      node.content.forEach((rowNode) => {
        if (rowNode.nodeType === BLOCKS.TABLE_ROW) {
          const rowCells = [];
          rowNode.content.forEach((cellNode) => {
            if (cellNode.nodeType === BLOCKS.TABLE_CELL || cellNode.nodeType === BLOCKS.TABLE_HEADER_CELL) {
              const cellText = cellNode.content
                .map((block) => {
                  if (block.nodeType === BLOCKS.PARAGRAPH) {
                    return block.content.map((textNode) => textNode.value).join('');
                  }

                  return '';
                })
                .join('\n');
              rowCells.push(cellText);
            }
          });
          tableRows.push(rowCells);
        }
      });
      tables.push(tableRows);
    }
  });

  return tables.length ? tables[0] : tables;
};
